/**
 * employerBulkRunMarker.js — Slice β rehydration helper.
 *
 * Mirrors the candidate-side `lib/runMarker` pattern: a tiny
 * sessionStorage record that lets a tab refresh, navigation, or
 * tab-return re-bind to an in-flight bulk evaluation job without
 * re-dispatching.
 *
 * Keyed by role_id. One in-flight bulk-eval job per role at a time
 * (Slice α only ever runs one parent coordinator per role).
 *
 * Strict-hold: zero backend coupling. Marker is advisory UI state
 * only; the DB is the source of truth.
 */

const KEY_PREFIX = 'emp_bulk_eval:';
const SCHEMA = 1;

function key(roleId) {
  return `${KEY_PREFIX}${roleId}:in_flight`;
}

function storage() {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    // Smoke-test access (Safari private mode throws on first use).
    const t = '__emp_bulk_marker_probe__';
    window.sessionStorage.setItem(t, '1');
    window.sessionStorage.removeItem(t);
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function setMarker(roleId, payload) {
  if (!roleId || !payload?.jobId) return false;
  const s = storage();
  if (!s) return false;
  try {
    s.setItem(key(roleId), JSON.stringify({
      schema:       SCHEMA,
      jobId:        payload.jobId,
      accountId:    payload.accountId || null,
      dispatchedAt: payload.dispatchedAt || Date.now(),
    }));
    return true;
  } catch {
    return false;
  }
}

export function getMarker(roleId) {
  if (!roleId) return null;
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(key(roleId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.schema !== SCHEMA || typeof parsed.jobId !== 'string') {
      // Malformed payload — drop it.
      s.removeItem(key(roleId));
      return null;
    }
    return parsed;
  } catch {
    // JSON corrupted — remove and pretend it wasn't there.
    try { s.removeItem(key(roleId)); } catch { /* ignore */ }
    return null;
  }
}

export function clearMarker(roleId) {
  if (!roleId) return false;
  const s = storage();
  if (!s) return false;
  try {
    s.removeItem(key(roleId));
    return true;
  } catch {
    return false;
  }
}

// Test-only: peek the key shape for hermetic tests.
export const __testing = { key, SCHEMA };
